import { BookOutlined, LockOutlined, RedoOutlined, UnlockOutlined, ContainerOutlined } from '@ant-design/icons'
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import Paper from 'components/@extended/Paper'
import { type FC, type ReactNode } from 'react'
import type { Period } from 'types/periods'
import { formatDisplayDate } from 'types/periods'

interface PeriodsTableViewProps {
  periods: Period[]
  isLoading?: boolean
  showActions?: boolean
  onClosePeriod?: (period: Period) => void
  onReopenPeriod?: (period: Period) => void
  onLockPeriod?: (period: Period) => void
  onLockedPeriodInfo?: () => void
  onManagePlans?: (period: Period) => void
  isSubmitting?: boolean
  effectiveDates?: Record<string, string>
  hasFutureEffectiveDate?: (period: Period) => { hasFuture: boolean; effectiveDate?: string; affectedPeriod?: Period }
  planCounts?: Record<string, number>
}

const PeriodsTableView: FC<PeriodsTableViewProps> = ({
  periods,
  isLoading = false,
  showActions = false,
  onClosePeriod,
  onReopenPeriod,
  onLockPeriod,
  onLockedPeriodInfo,
  onManagePlans,
  isSubmitting = false,
  effectiveDates,
  hasFutureEffectiveDate,
  planCounts,
}) => {
  const theme = useTheme()

  const renderPeriodRows = (period: Period, depth: number): ReactNode[] => {
    const rows: ReactNode[] = []

    const status = period.status || (period.isClosed ? 'Closed' : 'Open')

    const renderActions = () => {
      if (!showActions) return null

      if (status.toLowerCase() === 'locked') {
        return (
          <>
            <Tooltip title="Period is Locked">
              <IconButton size="medium" sx={{ color: theme.palette.info.main }} onClick={onLockedPeriodInfo}>
                <LockOutlined />
              </IconButton>
            </Tooltip>
            {planCount > 0 && onManagePlans && (
              <Tooltip title="Manage Plans">
                <IconButton
                  size="medium"
                  sx={{ color: theme.palette.primary.main }}
                  onClick={() => onManagePlans(period)}
                  disabled={isSubmitting}
                >
                  <ContainerOutlined />
                </IconButton>
              </Tooltip>
            )}
          </>
        )
      }

      if (!period.isClosed) {
        // Check if period has future effective dates (includes descendants)
        const futureEffectiveCheck = hasFutureEffectiveDate?.(period)
        const hasFutureDate = futureEffectiveCheck?.hasFuture || false
        const effectiveDate = futureEffectiveCheck?.effectiveDate
        const affectedPeriod = futureEffectiveCheck?.affectedPeriod

        const tooltipTitle = hasFutureDate
          ? `Period cannot be closed until the effective date has passed because there are still actuals with outstanding effective dates. Latest effective date: ${effectiveDate ? formatDisplayDate(effectiveDate) : 'Unknown'}${affectedPeriod && affectedPeriod.id !== period.id ? ` (from ${affectedPeriod.label})` : ''}`
          : 'Close Period'

        return (
          <>
            <Tooltip title={tooltipTitle}>
              <span>
                <IconButton
                  size="medium"
                  sx={{
                    color: hasFutureDate ? theme.palette.grey[400] : theme.palette.primary.main,
                    cursor: hasFutureDate ? 'not-allowed' : 'pointer',
                  }}
                  onClick={() => !hasFutureDate && onClosePeriod?.(period)}
                  disabled={hasFutureDate || isSubmitting}
                >
                  <BookOutlined />
                </IconButton>
              </span>
            </Tooltip>
            {planCount > 0 && onManagePlans && (
              <Tooltip title="Manage Plans">
                <IconButton
                  size="medium"
                  sx={{ color: theme.palette.primary.main }}
                  onClick={() => onManagePlans(period)}
                  disabled={isSubmitting}
                >
                  <ContainerOutlined />
                </IconButton>
              </Tooltip>
            )}
          </>
        )
      }

      return (
        <>
          <Tooltip title="Reopen Period">
            <IconButton
              size="medium"
              sx={{ color: theme.palette.success.main }}
              onClick={() => onReopenPeriod?.(period)}
              disabled={isSubmitting}
            >
              <RedoOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Lock Period">
            <IconButton
              size="medium"
              sx={{ color: theme.palette.warning.main }}
              onClick={() => onLockPeriod?.(period)}
              disabled={isSubmitting}
            >
              <UnlockOutlined />
            </IconButton>
          </Tooltip>
          {planCount > 0 && onManagePlans && (
            <Tooltip title="Manage Plans">
              <IconButton
                size="medium"
                sx={{ color: theme.palette.info.main }}
                onClick={() => onManagePlans(period)}
                disabled={isSubmitting}
              >
                <ContainerOutlined />
              </IconButton>
            </Tooltip>
          )}
        </>
      )
    }

    const planCount = planCounts && period.id ? planCounts[period.id] || 0 : 0
    const displayCount = planCount === 0 ? '-' : planCount

    rows.push(
      <TableRow key={period.id} role="checkbox" tabIndex={-1}>
        <TableCell style={{ paddingLeft: `${depth * 20 + 16}px`, width: 300 }}>{period.label}</TableCell>
        <TableCell style={{ width: 120 }}>{formatDisplayDate(period.startDate)}</TableCell>
        <TableCell style={{ width: 120 }}>{formatDisplayDate(period.endDate)}</TableCell>
        <TableCell style={{ width: 100 }}>{status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}</TableCell>
        <TableCell style={{ width: 100, textAlign: 'center' }}>{displayCount}</TableCell>
        {showActions && (
          <TableCell style={{ width: 120 }}>
            <Box display="flex" gap={0.5}>
              {renderActions()}
            </Box>
          </TableCell>
        )}
      </TableRow>
    )

    if (period.children) {
      period.children.forEach((child) => {
        rows.push(...renderPeriodRows(child, depth + 1))
      })
    }

    return rows
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <CircularProgress />
      </Box>
    )
  }

  if (!periods || periods.length === 0) {
    return (
      <Paper sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" color="text.secondary" mb={1}>
          No Child Periods
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This period has no child periods to manage.
        </Typography>
      </Paper>
    )
  }

  // Determine what periods to show
  // Show all periods passed in (including root and children)
  const periodsToShow = periods

  if (periodsToShow.length === 0) {
    return (
      <Paper sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" color="text.secondary" mb={1}>
          No Periods
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {periods.length === 1 ? 'This period has no child periods to manage.' : 'No periods are available.'}
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper>
      <TableContainer
        sx={{
          width: '100%',
        }}
      >
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead
            sx={{
              '& th': {
                borderTop: `1px solid ${theme.palette.divider}`,
                borderBottom: `2px solid ${theme.palette.divider} !important`,
              },
            }}
          >
            <TableRow>
              <TableCell sx={{ position: 'sticky !important', width: 250, minWidth: 250 }}>Period</TableCell>
              <TableCell sx={{ position: 'sticky !important', width: 100, minWidth: 100 }}>Start Date</TableCell>
              <TableCell sx={{ position: 'sticky !important', width: 100, minWidth: 100 }}>End Date</TableCell>
              <TableCell sx={{ position: 'sticky !important', width: 80, minWidth: 80 }}>Status</TableCell>
              <TableCell sx={{ position: 'sticky !important', width: 100, minWidth: 100, textAlign: 'center' }}>
                Plans
              </TableCell>
              {showActions && (
                <TableCell sx={{ position: 'sticky !important', width: 100, minWidth: 100 }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>{periodsToShow.map((period) => renderPeriodRows(period, 0)).flat()}</TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

export default PeriodsTableView
